import { Alert } from "@mui/material";
import { pageRouteUrls } from "../helper/pageRouteUrls";
import MobileNavigationbar from "../component/MobileNavigationbar";
import Navigationbar from "../component/Navigationbar";
import { TopHeaderBar } from "../component/topHeaderBar";
import { useLocation } from "react-router-dom";
import UserNavIcon from "./userNavIcon";
import ApplicationContext from "../contexts/ApplicationContext";
import { useContext, useEffect, useState } from "react";

import Container from "react-bootstrap/Container";
import Navbar from "react-bootstrap/Navbar";
import { useCurrUserUtility } from "../contexts/useCurrUserUtility";
import { useError } from "../contexts/ErrorContext";
import SearchHeader from "./searchHeader";
import SiteLogo from "../component/SiteLogo";
// Language switcher hidden for now — re-enable this import with the two usages below.
// import LanguageSwitcher from "../component/LanguageSwitcher";
import MenuIcon from "@mui/icons-material/Menu";
import CloseIcon from "@mui/icons-material/Close";

export interface HeaderProps {}

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { isAuth } = useCurrUserUtility();
  const location = useLocation();

  const { errorMessages } = useError();
  const { message, setMessage } = useContext(ApplicationContext)!;

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Clear message + close the mobile menu on route change
  useEffect(() => {
    setMessage(null);
    setMobileMenuOpen(false);
  }, [location.pathname]);

  // Auto-dismiss message after 5 s
  useEffect(() => {
    if (!message) return;
    const timer = setTimeout(() => setMessage(null), 5000);
    return () => clearTimeout(timer);
  }, [message]);

  const renderAlertContent = (html: string) => (
    <span dangerouslySetInnerHTML={{ __html: html }} />
  );

  return (
    <>
      {/* Header Top — uncomment if needed */}
      {/* <TopHeaderBar /> */}

      <div className="header-section">

        {/* ── Desktop Header ─────────────────────── */}
        <div className={`header-main ${scrolled ? "sticky" : ""}`}>
          <Container>
            <div className="header-main-wrapper">

              {/* Logo */}
              <Navbar.Brand href={pageRouteUrls.Home()} className="header-logo">
                <SiteLogo height={40} />
              </Navbar.Brand>

              {/* Mobile hamburger — opens the slide-in mobile menu (hidden on desktop) */}
              <button
                type="button"
                className="mobile-menu-toggle d-lg-none"
                aria-label="Open menu"
                aria-expanded={mobileMenuOpen}
                onClick={() => setMobileMenuOpen(true)}
                style={{
                  background: "none", border: "none", padding: 6, cursor: "pointer",
                  color: "#1a2e1a", display: "inline-flex", alignItems: "center",
                }}
              >
                <MenuIcon style={{ fontSize: 30 }} />
              </button>

              {/* ── Nav Menu — powered by Navigationbar ── */}
              <div className="header-menu d-none d-lg-block">
                <ul className="nav-menu">
                  <Navigationbar />
                </ul>
              </div>

              {/* Search — pushed to the right so search + language + user cluster together */}
              <div className="sidebar-widget widget-search mt-0" style={{ marginInlineStart: "auto" }}>
                <SearchHeader />
              </div>

              {/* Language switcher — hidden for now (re-enable to bring the AR/EN toggle back)
              <div className="header-language d-none d-lg-flex" style={{ alignItems: "center", marginInlineStart: 8 }}>
                <LanguageSwitcher />
              </div>
              */}

              {/* User icon / sign-in */}
              <div className="header-sign-in-up d-none d-lg-block">
                <ul>
                  <UserNavIcon />
                </ul>
              </div>

            </div>
          </Container>
        </div>

        {/* ── Alert messages ─────────────────────── */}
        {errorMessages &&
          errorMessages.map((msg, idx) => (
            <Alert
              variant="filled"
              key={`errorMsg${idx}`}
              severity="error"
              className="AlertMsg"
              onClose={() => setMessage(null)}
            >
              {renderAlertContent(msg)}
            </Alert>
          ))}

        {message?.success && (
          <Alert
            variant="filled"
            severity="success"
            className="AlertMsg"
            onClose={() => setMessage(null)}
          >
            {renderAlertContent(message.success.toString())}
          </Alert>
        )}
        {message?.error && (
          <Alert
            variant="filled"
            severity="error"
            className="AlertMsg"
            onClose={() => setMessage(null)}
          >
            {renderAlertContent(message.error.toString())}
          </Alert>
        )}
        {message?.info && (
          <Alert
            variant="filled"
            severity="info"
            className="AlertMsg"
            onClose={() => setMessage(null)}
          >
            {renderAlertContent(message.info.toString())}
          </Alert>
        )}
        {message?.warning && (
          <Alert
            variant="filled"
            severity="warning"
            className="AlertMsg"
            onClose={() => setMessage(null)}
          >
            {renderAlertContent(message.warning.toString())}
          </Alert>
        )}
      </div>

      {/* ── Mobile Menu ────────────────────────── */}
      {/* Backdrop — click outside to close (only rendered while open) */}
      {mobileMenuOpen && (
        <div
          className="mobile-menu-overlay"
          onClick={() => setMobileMenuOpen(false)}
          style={{
            position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)",
            zIndex: 998,
          }}
        />
      )}

      <div className={`mobile-menu ${mobileMenuOpen ? "open" : ""}`}>

        <a
          className="menu-close"
          href="#"
          onClick={(e) => { e.preventDefault(); setMobileMenuOpen(false); }}
        >
          <CloseIcon style={{ fontSize: 28 }} />
        </a>

        <div className="mobile-top">
          <p><i className="flaticon-phone-call"></i> <a href="tel:+12362047735">+1(236) 204-7735</a></p> 
          <p><i className="flaticon-email"></i> <a href="mailto:support@myedulink.net">support@myedulink.net</a></p>
        </div>

        <div className="mobile-sign-in-up">
          <ul>
            <UserNavIcon />
          </ul>
        </div>

        {/* Language switcher — hidden for now (re-enable to bring the AR/EN toggle back)
        <div className="mobile-language" style={{ padding: "10px 0" }}>
          <LanguageSwitcher />
        </div>
        */}

        {/* Mobile nav — same component, same role logic */}
        <MobileNavigationbar />

        <div className="mobile-social">
          <ul className="social">
            <li><a href="#"><i className="flaticon-facebook"></i></a></li>
            <li><a href="#"><i className="flaticon-twitter"></i></a></li>
            <li><a href="#"><i className="flaticon-skype"></i></a></li>
            <li><a href="#"><i className="flaticon-instagram"></i></a></li>
          </ul>
        </div>

      </div>
    </>
  );
};